import { ArrowUp } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import AutoResizeTextarea from '@/components/AutoResizeTextarea';
import { Button } from '@/components/ui/button';

const POPOVER_WIDTH = 320;
const POPOVER_HEIGHT = 170;
const EDGE_GAP = 8;

interface Anchor {
    left: number;
    top: number;
    width: number;
    height: number;
}

interface DashboardCommentPopoverProps {
    anchor: Anchor;
    container: { width: number; height: number };
    breadcrumb: string;
    onSubmit: (text: string) => void;
    onCancel: () => void;
}

export function DashboardCommentPopover({
    anchor,
    container,
    breadcrumb,
    onSubmit,
    onCancel
}: DashboardCommentPopoverProps) {
    const [text, setText] = useState('');
    const popoverRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onCancel();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onCancel]);

    const submit = () => {
        const trimmed = text.trim();
        if (trimmed) onSubmit(trimmed);
    };

    const left = Math.min(
        Math.max(EDGE_GAP, anchor.left),
        Math.max(EDGE_GAP, container.width - POPOVER_WIDTH - EDGE_GAP)
    );
    const below = anchor.top + anchor.height + EDGE_GAP;
    const fitsBelow = below + POPOVER_HEIGHT <= container.height;
    const top = fitsBelow
        ? below
        : Math.max(EDGE_GAP, anchor.top - EDGE_GAP - POPOVER_HEIGHT);

    return (
        <div
            ref={popoverRef}
            style={{ left, top, width: POPOVER_WIDTH }}
            className="z-30 absolute flex flex-col gap-3 bg-background shadow-xl p-3 border border-border rounded-xl animate-in duration-150 fade-in-0 zoom-in-95"
        >
            <p className="text-muted-foreground text-xs truncate">
                {breadcrumb}
            </p>
            <AutoResizeTextarea
                autoFocus
                value={text}
                onChange={(event) => setText(event.target.value)}
                onKeyDown={(event) => {
                    if (
                        event.key === 'Enter' &&
                        !event.shiftKey &&
                        !event.nativeEvent.isComposing
                    ) {
                        event.preventDefault();
                        submit();
                    }
                }}
                placeholder="Ask the agent to change this..."
                className="bg-transparent focus:outline-hidden w-full placeholder:text-muted-foreground text-sm resize-none leading-5"
                maxHeight={96}
            />
            <div className="flex justify-end items-center gap-2">
                <Button variant="ghost" size="sm" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    size="icon-sm"
                    onClick={submit}
                    disabled={!text.trim()}
                    aria-label="Send"
                >
                    <ArrowUp />
                </Button>
            </div>
        </div>
    );
}
